import styled from "styled-components";
import Logo from "../ui-v2/Logo";
import Dropdown from "../ui-v2/Dropdown";
import DropdownUser from "../ui-v2/DropdownUser";
import { useTypedDispatch } from "../../hooks/useTypedDispatch";
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ROUTES } from "../../router";
import {
  setLastAccessedOrganizations,
  TOrganization,
} from "../../redux/reducers/organizations";
import { useNavigationManager } from "../../hooks/useNavigationManager";
import { TUserData } from "../../redux/reducers/userData";
import {
  getQuerystringParams,
  handleGetDataFromLocalStorage,
  handleSetDataToLocalStorage,
} from "../../utils/miscellaneous";
import { localStorageConstants } from "../../utils/constants";
// import { parse } from "date-fns";
import { TAccount } from "../../redux/reducers/account";

import Button from "../ui-v2/Button";
import QuickAccessMenu from "../ui-v2/Menus/QuickAccessMenu";
import {
  fetchOrganizations,
  setSelectedOrganizationThunk,
} from "../../redux/actions";
import axios, { CancelTokenSource } from "axios";
import { useTypedSelector } from "../../hooks/useTypedSelector";
import apiService from "../../services/api";

const Container = styled.header`
  display: flex;
  justify-content: space-between;

  background: var(--color-light-0);
  width: 100vw;
  height: 72px;

  position: fixed;
  top: 0;
  z-index: 99999;

  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1);

  .right-container {
    display: flex;
    align-items: center;

    & > div:first-child {
      width: 328px;
      margin-right: 16px;

      & > div > button {
        height: 40px;
        min-height: unset;

        &::before {
          top: 5px;
        }

        & svg {
          top: 8px;
        }

        & p.content {
          top: 15px;
        }
      }
    }

    & > button {
      width: 40px;
      height: 40px;
      margin-right: 16px;

      &:active {
        background: var(--color-light-100);
      }

      &:hover {
        background: var(--color-light-50);
      }

      & svg {
        width: 22px;
        height: 22px;
      }
    }
  }
`;

const Subcontainer = styled.div`
  display: flex;
  align-items: center;

  margin-left: 24px;

  .homeLink {
    padding: 0;
    cursor: pointer;

    &:hover {
      background: transparent;
    }
  }

  .separator {
    margin: 0 24px;
    height: 40px;
    width: 1px;
    background: var(--color-light-200);
  }

  .tab-area {
    display: flex;

    button {
      font-family: var(--font-paragraph-default);
      color: var(--color-text-labels);
      font-size: 16px;
      height: 71px;
      border-radius: 8px 8px 0px 0px;
      cursor: pointer;

      &:nth-child(2) {
        margin-left: 8px;
      }

      &:hover {
        background: var(--color-light-100);
      }

      &.active {
        color: var(--color-main-rose);
        font-family: var(--font-paragraph-bold);
        border-bottom: 3px solid var(--color-main-rose);
      }
    }
  }
`;

const Header: React.FC = () => {
  const previouslyLoadedOrganizations = useTypedSelector(
    (state) => state.organizations.previouslyLoadedOrganizations
  );
  let lastAccessedOrganizations = useTypedSelector(
    (state) => state.organizations.lastAccessedOrganizations
  );
  // Fallback to localStorage if Redux state is empty
  if (!lastAccessedOrganizations || lastAccessedOrganizations.length === 0) {
    const localLastAccessed = handleGetDataFromLocalStorage(
      localStorageConstants.LAST_ACCESSED_ORGANIZATIONS
    );
    if (localLastAccessed && Array.isArray(localLastAccessed)) {
      lastAccessedOrganizations = localLastAccessed;
    }
  }
  const [organizations, setOrganizations] = useState<TOrganization[]>([]);
  // Track context changes for Dropdown remount
  const [, /* orgListVersion */ setOrgListVersion] = useState(0);
  const organizationsCountRef = useRef(0);
  const [, /* selectOrganization*/ setSelectOrganization] =
    useState<TOrganization | null>(null);

  // const [activeTabIndex, setActiveTabIndex] = useState(0);

  const [shoulShowQuickAccessMenu, setShouldShowQuickAccessMenu] =
    useState(false);

  const organizationRef = useRef<TOrganization[]>([]);
  const searchPhraseRef = useRef<string | undefined>();
  const quickAccessButtonRef = useRef<HTMLButtonElement | null>(null);

  // Ref to store setLoading from Dropdown
  const setItemLoadingRef = useRef<
    null | ((loading: Record<string, boolean>) => void)
  >(null);

  const cancelTokenSource = useRef<CancelTokenSource | null>(null);

  const dispatch = useTypedDispatch();

  const { setQuerystring, goTo } = useNavigationManager();

  const reduxQuerystringState = useTypedSelector((state) => state.querystring);

  const selectedOrganization = useTypedSelector(
    (state) => state.organizations.selectedOrganization
  );

  const [user, setUser] = useState<TUserData | null>(null);

  const mergedOrganizations = [...organizations];

  const companyNames = useMemo(
    () => mergedOrganizations?.map((org) => org.company_name) ?? [""],
    [mergedOrganizations]
  );
  const cnpjs = useMemo(
    () => mergedOrganizations?.map((org) => org.cnpj) ?? [""],
    [mergedOrganizations]
  );

  const handleOptions = (option: string) =>
    option === "companyName" ? companyNames : cnpjs;

  // Utility: deep compare two objects (simple version)
  function areQuerystringsEqual(a: any, b: any) {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  const handleFetchOrganizations = useCallback(async () => {
    const querystring = getQuerystringParams();

    const localSelectAccount = handleGetDataFromLocalStorage(
      localStorageConstants.SELECT_ACCOUNT
    ) as TAccount;
    const localSelectOrganization = handleGetDataFromLocalStorage(
      localStorageConstants.SELECT_ORGANIZATION
    ) as TOrganization;

    if (
      querystring.account_id &&
      querystring.account_id?.length > 0 &&
      querystring.account_id !== localSelectAccount?.uuid
    ) {
      handleSetDataToLocalStorage(
        localStorageConstants.SELECT_ACCOUNT,
        querystring.account_id
      );
    } else if (localSelectAccount) {
      querystring.account_id = localSelectAccount.uuid;
    }

    if (
      querystring.organization_id &&
      querystring.organization_id.length > 0 &&
      querystring.organization_id !== localSelectOrganization?.uuid
    ) {
      handleSetDataToLocalStorage(
        localStorageConstants.SELECT_ORGANIZATION,
        querystring.organization_id
      );
    } else if (localSelectOrganization) {
      querystring.organization_id = localSelectOrganization.uuid;
    }

    cancelTokenSource.current = axios.CancelToken.source();

    // Only call setQuerystring if different from Redux state
    if (!areQuerystringsEqual(querystring, reduxQuerystringState)) {
      setQuerystring(querystring);
    }

    if (querystring.account_id?.length > 0) {
      const organizationsResponse = await dispatch(
        fetchOrganizations({
          cancelToken: cancelTokenSource.current.token,
        })
      ).unwrap();

      organizationsCountRef.current = organizationsResponse?.count ?? 0;
      // API organizations
      const apiOrganizations = organizationsResponse?.results ?? [];
      // Filter lastAccessedOrganizations to exclude orgs already in API response
      const filteredLastAccessed = lastAccessedOrganizations.filter(
        (org) => !apiOrganizations.some((o) => o.uuid === org.uuid)
      );
      // Merge, API orgs first, then last accessed
      const mergedOrganizations = [
        ...apiOrganizations,
        ...filteredLastAccessed,
      ];

      organizationRef.current = mergedOrganizations;
      setOrganizations(mergedOrganizations);
      // Only increment version on context switch, not on search
      setOrgListVersion((v) => v + 1);

      if (localSelectOrganization) {
        let selectOrg = organizationRef.current.filter(
          (organizations) => organizations.uuid === localSelectOrganization.uuid
        );

        if (selectOrg.length > 0) {
          handleSetDataToLocalStorage(
            localStorageConstants.SELECT_ORGANIZATION,
            selectOrg[0]
          );
        }

        dispatch(
          setSelectedOrganizationThunk({
            selectedOrganization: selectOrg[0],
            cancelToken: cancelTokenSource.current.token,
          })
        );

        if (
          querystring.organization_id &&
          querystring.organization_id.length > 0 &&
          querystring.organization_id !== localSelectOrganization.uuid
        ) {
          let qsOrganization = organizationRef.current.filter(
            (organizations) =>
              organizations.uuid === querystring.organization_id
          );

          if (qsOrganization.length > 0) {
            handleSetDataToLocalStorage(
              localStorageConstants.SELECT_ORGANIZATION,
              qsOrganization[0]
            );

            const newQuerystring = {
              ...querystring,
              organization_id: qsOrganization[0]?.uuid,
            };
            if (!areQuerystringsEqual(newQuerystring, reduxQuerystringState)) {
              setQuerystring(newQuerystring);
            }

            dispatch(
              setSelectedOrganizationThunk({
                selectedOrganization: qsOrganization[0],
                cancelToken: cancelTokenSource.current.token,
              })
            );

            setSelectOrganization(qsOrganization[0]);
          }
        } else {
          if (!areQuerystringsEqual(querystring, reduxQuerystringState)) {
            setQuerystring(querystring);
          }

          dispatch(
            setSelectedOrganizationThunk({
              selectedOrganization: localSelectOrganization,
              cancelToken: cancelTokenSource.current.token,
            })
          );

          setSelectOrganization(localSelectOrganization);
        }
      } else {
        if (organizationsResponse?.results[0]) {
          handleSetDataToLocalStorage(
            localStorageConstants.SELECT_ORGANIZATION,
            organizationsResponse.results[0]
          );

          const newQuerystring = {
            ...querystring,
            organization_id: organizationsResponse.results[0].uuid,
          };
          if (!areQuerystringsEqual(newQuerystring, reduxQuerystringState)) {
            console.log("stage 2: querystring", newQuerystring);
            setQuerystring(newQuerystring);
          }

          dispatch(
            setSelectedOrganizationThunk({
              selectedOrganization: organizationsResponse.results[0],
              cancelToken: cancelTokenSource.current.token,
            })
          );

          setSelectOrganization(organizationsResponse.results[0]);
        }
      }

      const localLastAccessedOrganizations = handleGetDataFromLocalStorage(
        localStorageConstants.LAST_ACCESSED_ORGANIZATIONS
      );
      if (localLastAccessedOrganizations) {
        dispatch(setLastAccessedOrganizations(localLastAccessedOrganizations));
      }
    }
  }, [reduxQuerystringState]);

  const handleMenuOnSelect = async (selected: string) => {
    // Set loading for the selected item (show spinner)
    if (setItemLoadingRef.current) {
      setItemLoadingRef.current({ [selected]: true });
    }

    searchPhraseRef.current = undefined;

    let selectedOrganization = await new Promise<TOrganization | undefined>(
      (resolve) => {
        setTimeout(() => {
          resolve(
            organizations?.find(
              (organization) =>
                organization.cnpj === selected ||
                organization.company_name === selected
            )
          );
        }, 2000);
      }
    );

    if (!selectedOrganization) {
      const querystring = getQuerystringParams();
      const searchParam = selected.replace(/[./-]/g, "");
      try {
        const response = await apiService.getOrganizations(
          querystring.account_id,
          (cancelTokenSource.current as CancelTokenSource).token,
          undefined,
          searchParam // use uuid if found, else fallback to cnpj
        );

        if (
          response &&
          response.data &&
          response.data.results &&
          response.data.results.length > 0
        ) {
          const org = response.data.results[0];
          selectedOrganization = org;
        }
      } catch (error) {
        console.log("Error fetching organization:", error);
      }
    }

    const localLastAccessedOrganizations = handleGetDataFromLocalStorage(
      localStorageConstants.LAST_ACCESSED_ORGANIZATIONS
    ) as TOrganization[];

    if (selectedOrganization) {
      handleSetDataToLocalStorage(
        localStorageConstants.SELECT_ORGANIZATION,
        selectedOrganization
      );

      dispatch(
        setSelectedOrganizationThunk({
          selectedOrganization,
          cancelToken: (cancelTokenSource.current as CancelTokenSource).token,
        })
      );

      const querystring = { ...reduxQuerystringState };
      querystring.organization_id = selectedOrganization.uuid;
      setQuerystring(querystring);
    } else {
      // Last chance: check previouslyLoadedOrganizations for a match
      let fallbackOrgs: TOrganization[] = [];
      if (
        previouslyLoadedOrganizations &&
        previouslyLoadedOrganizations.length > 0
      ) {
        const found = previouslyLoadedOrganizations.find(
          (org) => org.cnpj === selected || org.company_name === selected
        );
        if (found) {
          fallbackOrgs = [found];
        }
      }
      if (fallbackOrgs.length > 0) {
        handleSetDataToLocalStorage(
          localStorageConstants.LAST_ACCESSED_ORGANIZATIONS,
          fallbackOrgs
        );
        dispatch(setLastAccessedOrganizations(fallbackOrgs));
      } else if (localLastAccessedOrganizations) {
        handleSetDataToLocalStorage(
          localStorageConstants.LAST_ACCESSED_ORGANIZATIONS,
          []
        );
        dispatch(setLastAccessedOrganizations([]));
      }
      // Remove loading spinner before reload
      if (setItemLoadingRef.current) {
        setItemLoadingRef.current({});
      }
      window.location.reload();
      return;
    }

    // Remove loading spinner after all logic is done
    if (setItemLoadingRef.current) {
      setItemLoadingRef.current({});
    }
  };

  const handleOnSearch = async (searchPhrase: string) => {
    const querystring = getQuerystringParams();
    searchPhraseRef.current = searchPhrase;

    // Check if this result is for the latest search
    if (searchPhrase !== searchPhraseRef.current) {
      // This result is outdated, do not update state
      return;
    }

    setTimeout(async () => {
      const response = await apiService.getOrganizations(
        querystring.account_id,
        (cancelTokenSource.current as CancelTokenSource).token,
        undefined,
        searchPhrase
      );
      // Check if this result is for the latest search
      if (searchPhrase !== searchPhraseRef.current) {
        // This result is outdated, do not update state
        return;
      }

      if (response) {
        // setOrganizations(response.data.results);
        searchPhraseRef.current = undefined;
      }
    }, 20);
  };

  useEffect(() => {
    setTimeout(() => {
      handleFetchOrganizations();

      const localUserData = handleGetDataFromLocalStorage(
        localStorageConstants.USER_DATA
      ) as TUserData;

      setUser(localUserData);
    }, 200);

    return () => {
      if (cancelTokenSource.current) cancelTokenSource.current.cancel();
    };
  }, [window.location.search]);

  return (
    <Container>
      <Subcontainer>
        <button
          className="homeLink"
          onClick={() => {
            // setActiveTabIndex(0);
            const qsCopy = { ...getQuerystringParams() };

            if (qsCopy.search) {
              delete qsCopy.search;
            }
            if (qsCopy.page) {
              qsCopy.page = 1;
            }
            if (qsCopy.modal) {
              delete qsCopy.modal;
            }

            delete qsCopy.start_date;
            delete qsCopy.end_date;
            qsCopy.period = "all";

            setQuerystring(qsCopy);
            goTo(ROUTES.HOME, qsCopy);
          }}
        >
          <Suspense fallback={<div style={{ width: 191, height: 40 }}></div>}>
            <Logo />
          </Suspense>
        </button>
      </Subcontainer>
      <div className="right-container">
        <Dropdown
          button={{
            variant: "labeled",
            labels: handleOptions("companyName"),
            placeholder: "Organização",
            showPlaceholder: false,
          }}
          menu={{
            variant: "labeled",
            labels: handleOptions("companyName"),
            options: handleOptions("cnpj"),
            search: true,
            searchPhraseOnChange: handleOnSearch,
            optGroup: ["Últimos acessados", "Todos"],
            onSelect: handleMenuOnSelect,
            hasInfiniteScrolling: {
              variant: "organizations",
              totalElements: organizationsCountRef.current,
              onIntersect(data, elementsAmount) {
                organizationsCountRef.current = elementsAmount;
                if (
                  searchPhraseRef.current === undefined &&
                  organizations.length < data.length
                ) {
                  setOrganizations(data);
                }
              },
              searchParam: searchPhraseRef.current,
            },
          }}
          onItemLoadingChange={(_loading, setLoading) => {
            setItemLoadingRef.current = setLoading;
          }}
          selectedOrganization={selectedOrganization}
        />
        <Button
          onClick={() => {
            const quickAccessRef = document.getElementById(
              "quick-access-menu-button"
            );
            if (quickAccessRef)
              quickAccessButtonRef.current =
                quickAccessRef as HTMLButtonElement;
            setShouldShowQuickAccessMenu((before) => !before);
          }}
          icon="onlyIcon"
          iconType="layerGroup"
          variant="outline"
          id="quick-access-menu-button"
        />
        <QuickAccessMenu
          $ref={quickAccessButtonRef.current}
          $left={quickAccessButtonRef.current?.getBoundingClientRect().left}
          shouldShow={shoulShowQuickAccessMenu}
          onClickAway={() => {
            setShouldShowQuickAccessMenu(false);
          }}
        />
        {user && (
          <DropdownUser
            key={user?.uuid ?? ""}
            email={user?.email ?? ""}
            icon={
              (user &&
                (user.first_name[0] ?? "") + (user.last_name[0] ?? "")) ??
              ""
            }
            name={user?.first_name + " " + user?.last_name}
          />
        )}
      </div>
    </Container>
  );
};

export default Header;
